import React, { useState } from 'react';

import DownloadButton from 'js/canonical_cards/work_card/download_button';
import DownloadButtonSearchRedesign from 'js/canonical_cards/work_card/download_button_search_redesign';
import LibraryButtonWrapper from '../shared/buttons/library_button_wrapper.wjs.jsx';
import LibraryButton from './library_button';
import LibraryButtonSearchRedesign from './library_button_search_redesign';
import BulkDownloadButton from './bulk_download_button';
import MoreActionsButton from './more_actions_button';
import TranslateButton from './translate_button';
import { isCoronavirusWork } from 'js/coronavirus/coronavirus_research_detector';
import { useDownload } from 'js/bulk_download/useDownload';
import { ReaderMessageModal } from 'js/liswp/modals_ds/ReaderMessageModal';
import FallbackBulkDownloadUpsellModalControl from 'js/bulk_download/FallbackBulkDownloadUpsellModal/FallbackBulkDownloadUpsellModalControl';
import { ReaderMessageContext } from 'js/liswp/work_actions/ReaderMessageContext';
import { ToastProvider } from 'js/design_system/toasts/ToastProvider';
import { Work as HookWork } from 'js/liswp/types';

// The canonical work shape passed from Redux / work_card / search_work_card.
// Differs from liswp Work (plural downloadableAttachments, optional slug, etc.).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface CanonicalWork {
  id: number;
  title: string;
  slug?: string;
  internalUrl?: string;
  contentType?: string;
  isBookmarked?: boolean;
  language?: string;
  // Server-computed (WorkPlusAuthorsSerializer#translation_supported), the
  // source of truth for whether the Translate button should render.
  translationSupported?: boolean;
  downloadableAttachments: Array<{
    downloadUrl: string;
    id: number;
    scribdThumbnailUrl?: string;
  }>;
  authors: Array<{
    id: number | string;
    displayName: string;
    photo?: string;
    url?: string;
  }>;
  metadata?: {
    abstract?: string;
    publicationDate?: { day?: number; month?: number; year?: number };
    publicationName?: string;
  };
  researchInterests?: Array<{
    id: number;
    name: string;
    url: string;
  }>;
  bulkDownloadPurchased?: boolean;
  filterCounts?: Record<string, number>;
  alreadySharedUserIds?: unknown[];
  shareSuggestions?: unknown[];
}

interface Props {
  premium: boolean;
  work: CanonicalWork;
  source: string;
  loggedIn?: boolean;
  showSearchRedesign?: boolean;
  showBulkDownloadModal: boolean;
  showBulkDownloadButton: boolean;
  reportWork: (...args: unknown[]) => void;
  loadWorkShareSuggestions: (...args: unknown[]) => void;
  addToLibrary: (...args: unknown[]) => void;
  removeFromLibrary: (...args: unknown[]) => void;
  enableAddToWorkCollection?: boolean;
  addToWorkCollectionModalProps?: {
    startAddingItemToCollection?: (...args: unknown[]) => void;
    workCollectionsWithWorks?: Record<
      string,
      { id: number; name: string; work_count?: number; works?: unknown[] }
    >;
  };
  showRemoveFromWorkCollection?: boolean;
  removeFromWorkCollection?: () => void;
  showTranslateButton?: boolean;
  userLanguages?: string[];
}

/** Adapt the canonical work shape to the liswp Work type consumed by useDownload. */
function toHookWork(work: CanonicalWork): HookWork {
  const attachment = work.downloadableAttachments[0];
  return {
    id: work.id,
    title: work.title,
    slug: work.slug ?? `work-${work.id}`,
    internalUrl: work.internalUrl ?? '',
    isBookmarked: work.isBookmarked ?? false,
    authors: work.authors.map((a) => ({
      id: typeof a.id === 'string' ? parseInt(a.id, 10) : a.id,
      displayName: a.displayName,
      photo: a.photo,
      url: a.url ?? '',
    })),
    researchInterests: (work.researchInterests ?? []).map((ri) => ({
      ...ri,
      followerCount: 0,
      isFollowing: false,
    })),
    downloadableAttachment: attachment
      ? { id: attachment.id, downloadUrl: attachment.downloadUrl, pageCount: 0 }
      : undefined,
  };
}

/**
 * Inner component that calls useDownload. Must be rendered inside a
 * ToastProvider (for auto-bookmark toasts) and ReaderMessageContext.Provider.
 */
function DownloadSection({
  work,
  source,
  premium,
  loggedIn,
  showBulkDownloadModal,
  showReaderMessageModal,
  showSearchRedesign,
}: {
  work: CanonicalWork;
  source: string;
  premium: boolean;
  loggedIn: boolean;
  showBulkDownloadModal: boolean;
  showReaderMessageModal: boolean;
  showSearchRedesign: boolean;
}): React.ReactElement | null {
  const hookWork = React.useMemo(() => toHookWork(work), [work]);

  const {
    onDownloadClick: handleDownloadClick,
    downloadAnchorProps,
    readerMessageModal,
    lookbackUpsell,
  } = useDownload({
    work: hookWork,
    source,
    premium,
    gateDownload: true,
    shouldAutoBookmark: loggedIn,
    showAutoBookmarkToast: loggedIn,
    showReaderMessageModal,
    showBulkDownloadUpsell: showBulkDownloadModal,
    bulkDownloadPurchased: !!work.bulkDownloadPurchased,
  });

  return (
    <>
      {/* eslint-disable-next-line no-restricted-syntax -- Hidden download anchor for programmatic clicks */}
      <a {...downloadAnchorProps} />
      {showSearchRedesign ? (
        <DownloadButtonSearchRedesign
          styleAsDownloaded={false}
          onDownloadClick={handleDownloadClick}
          showWhiteBackground={false}
        />
      ) : (
        <DownloadButton
          styleAsDownloaded={false}
          onDownloadClick={handleDownloadClick}
        />
      )}
      {lookbackUpsell.open && (
        <FallbackBulkDownloadUpsellModalControl
          onClose={lookbackUpsell.close}
          workId={lookbackUpsell.workId}
          trialPricingCopy={lookbackUpsell.trialPricingCopy}
          impressionSource={lookbackUpsell.impressionSource}
        />
      )}
      {readerMessageModal.open && (
        <ReaderMessageModal
          primaryAuthor={readerMessageModal.primaryAuthor}
          onClose={readerMessageModal.close}
          workId={readerMessageModal.workId}
          attachmentId={readerMessageModal.attachmentId}
          readerMessageSource={readerMessageModal.source}
        />
      )}
    </>
  );
}

export default function WorkActions({
  premium: premiumProp,
  work,
  source,
  loggedIn = true,
  showSearchRedesign = false,
  showBulkDownloadModal,
  showBulkDownloadButton,
  reportWork,
  loadWorkShareSuggestions,
  addToLibrary,
  removeFromLibrary,
  enableAddToWorkCollection,
  addToWorkCollectionModalProps,
  showRemoveFromWorkCollection,
  removeFromWorkCollection,
  showTranslateButton = false,
  userLanguages = [],
}: Props): React.ReactElement {
  const downloadable = work.downloadableAttachments[0] != null;
  const premium = premiumProp || isCoronavirusWork(work);
  const isOpenalexWork = work.contentType === 'OpenalexWork';
  // Server is the source of truth for translatability (mirrors
  // Translation::PdfGenerator#supported?), so the button can't render for a
  // work the server will then refuse to translate.
  const translatable = work.translationSupported === true;
  const [readerMessageLimitReached, setReaderMessageLimitReached] =
    useState(false);

  return (
    <div className="work-card--action-row-wrapper">
      <div className="work-card--actions-wrapper">
        {isOpenalexWork && (
          <DownloadButton
            styleAsDownloaded={false}
            isExternalSource={true}
            onDownloadClick={() =>
              window.open(work.internalUrl, '_blank', 'noreferrer')
            }
          />
        )}
        {downloadable && (
          <ToastProvider>
            <ReaderMessageContext.Provider
              value={{
                readerMessageLimitReached,
                setReaderMessageLimitReached,
                citerightMarketingModalEligible: false,
                setCiterightMarketingModalEligible: () => {},
              }}
            >
              <DownloadSection
                work={work}
                source={source}
                premium={premium}
                loggedIn={loggedIn}
                showBulkDownloadModal={showBulkDownloadModal}
                showReaderMessageModal={
                  loggedIn && (!showBulkDownloadModal || premium)
                }
                showSearchRedesign={showSearchRedesign}
              />
            </ReaderMessageContext.Provider>
          </ToastProvider>
        )}
        {downloadable && loggedIn && (premium || showBulkDownloadButton) && (
          <BulkDownloadButton
            // BulkDownloadButton has its own narrower Work/source types;
            // the canonical work shape is a superset so the cast is safe.
            work={
              work as React.ComponentProps<typeof BulkDownloadButton>['work']
            }
            source={
              source as React.ComponentProps<
                typeof BulkDownloadButton
              >['source']
            }
            premium={premium}
            showSearchRedesign={showSearchRedesign}
          />
        )}
        {showTranslateButton && translatable && (
          <TranslateButton
            work={work}
            premium={premium}
            userLanguages={userLanguages}
          />
        )}
        {loggedIn ? (
          <LibraryButtonWrapper
            work={work}
            source={source}
            addToLibrary={addToLibrary}
            removeFromLibrary={removeFromLibrary}
          >
            {/* Props (isBookmarked, onLibraryClick) are injected by
                LibraryButtonWrapper via cloneElement at runtime. */}
            {showSearchRedesign ? (
              // @ts-expect-error — props injected via cloneElement
              <LibraryButtonSearchRedesign />
            ) : (
              <LibraryButton />
            )}
          </LibraryButtonWrapper>
        ) : null}
        {loggedIn && !showSearchRedesign && !isOpenalexWork && (
          <MoreActionsButton
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            work={work as any}
            reportWork={reportWork}
            loadWorkShareSuggestions={loadWorkShareSuggestions}
            source={source}
            enableAddToWorkCollection={enableAddToWorkCollection}
            addToWorkCollectionModalProps={addToWorkCollectionModalProps}
          />
        )}
      </div>
      {showRemoveFromWorkCollection && (
        <div className="work-card--remove-from-list-wrapper">
          <div
            className="work-card--remove-from-list-button"
            onClick={removeFromWorkCollection}
          >
            <div className="work-card--remove-from-list-icon">
              <i className="fa fa-minus-circle"></i>
            </div>
            <div className="work-card--remove-from-list-text">
              Remove from List
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
